// ─────────────────────────────────────────────────────────────────────────────
// tap_effects.ts — Reusable tap-feedback animations
//
// A small library of named, ready-to-use tap visuals so the rest of the
// codebase can spawn the same effect without re-implementing the math.
//
// Catalog (so far):
//
//   'ring'  — outline expanding circle. Same math the SelectionWidget's
//             button-press effect uses. Subtle, non-occluding; good for
//             contact-time feedback or universal tap echo.
//             Default color: '#3a2010' (ink tone). 240 ms.
//
//   'burst' — solid filled disc that expands from r=4 → r=12 with a
//             bright alpha flash on impact then fade. Migrated verbatim
//             from the `_tapGhosts` consumed-state rendering in
//             canvas_app.ts. Has the "pinch / drum-hit" feel.
//             Default color: '#c8a84a' (golden). 300 ms.
//
// Coordinates are canvas-absolute (caller applies layout offset before
// calling `push`). All effects are gated by `setTapEffectsEnabled` so a
// single Settings → Visuals → Animations toggle silences the whole layer.
// ─────────────────────────────────────────────────────────────────────────────

/** Effect kinds available for the per-tap visual.
 *
 *    'ring'         — thin expanding outline circle.
 *    'burst'        — solid filled disc that pops then fades.
 *    'glow'         — soft radial-gradient pulse (default).
 *    'invoke-glow'  — same as 'glow' with a richer color arrangement
 *                     and longer lifetime; used for invocation-
 *                     resolving "climax" taps. Internal — selected by
 *                     the canvas when an invocation lands, never by
 *                     the user.
 *
 *  All kinds run the same animation for neutral and climax taps; the
 *  caller dials the colour and scale to highlight the climax. */
export type TapEffectKind = 'ring' | 'burst' | 'glow' | 'invoke-glow';

interface TapEffectInstance {
  kind: TapEffectKind;
  cx: number;       // canvas-absolute
  cy: number;
  color: string;
  startedAt: number;
  /** Multiplier on the kind's base radius. Used to reflect
   *  contact-force / impact (pen pressure) — firmer strikes spawn
   *  bigger rings. Defaults to 1. */
  scale: number;
}

// ── Per-kind constants ──────────────────────────────────────────────────────

/** Visual lifetime — kept in sync with the cadence engine's
 *  `SINGLE_TAP_TIMEOUT_MS`. The animation lasts as long as the engine
 *  is still treating this tap as potentially "the start of a sequence";
 *  once it expires, both the listening window and the visual end at the
 *  same moment. Update both constants together. */
const TAP_FX_DURATION_MS = 320;

const RING_BASE_R = 14;
const RING_DEFAULT_COLOR = '#3a2010';

const BURST_BASE_R = 4;
const BURST_PEAK_R = 12;
const BURST_DEFAULT_COLOR = '#c8a84a';

/** glow / invoke-glow: radial-gradient pulse. Neutral taps use a
 *  faint white core; the invoke-glow variant ramps to the invocation
 *  tint at the halo for a richer climax mark. */
const GLOW_BASE_R = 5;
const GLOW_PEAK_R = 16;
const GLOW_DURATION_MS = 380;

const INVOKE_GLOW_BASE_R = 6;
const INVOKE_GLOW_PEAK_R = 22;
const INVOKE_GLOW_DURATION_MS = 480;

/** Neutral tap colour — faint warm white. Used when the tap isn't a
 *  resolving tap of any invocation. */
export const TAP_NEUTRAL_COLOR = '#f0e8d0';

/** Palette for the resolve-tap climax — picks per invocation. */
export const INVOKE_GLOW_CAST_COLOR  = '#7a5fa8';   // purple — cast (thrice-knock)
export const INVOKE_GLOW_STYLUS_COLOR = '#5f7fa8';  // blue   — stylus menu

/** Pulse-themed accents (cadence S/L feedback). Page-ink palette —
 *  desaturated, a touch darker than pure web colours, so they read on
 *  parchment without screaming. */
export const TAP_RING_SHORT_COLOR = '#5a3a8a';   // muted purple
export const TAP_RING_LONG_COLOR  = '#3a5a8a';   // muted blue
export const TAP_BURST_SHORT_COLOR = '#7a5fa8';  // brighter purple
export const TAP_BURST_LONG_COLOR  = '#5f7fa8';  // brighter blue

// ── Master gate ─────────────────────────────────────────────────────────────

let _enabled = true;

/** Master switch for the tap-effects layer. Wired from
 *  Settings → Visuals → Animations. Selection / cadence still happen;
 *  only the visual flourish is suppressed. */
export function setTapEffectsEnabled(v: boolean): void { _enabled = v; }

// ── Layer ───────────────────────────────────────────────────────────────────

export class TapEffectLayer {
  private _items: TapEffectInstance[] = [];

  /** Spawn an effect. Coordinates must be canvas-absolute.
   *  `scale` (default 1) multiplies the kind's base radius — a way to
   *  reflect impact force at the call site without the layer needing
   *  to know what "force" means upstream (pressure, energy, …). */
  push(
    kind: TapEffectKind,
    cx: number, cy: number,
    color?: string,
    scale: number = 1,
  ): void {
    if (!_enabled) return;
    this._items.push({
      kind,
      cx, cy,
      color: color ?? defaultColorFor(kind),
      startedAt: performance.now(),
      scale,
    });
  }

  /** Render every active effect, dropping any that finished. */
  draw(ctx: CanvasRenderingContext2D): void {
    const now = performance.now();
    this._items = this._items.filter(it => {
      const lifetime = lifetimeFor(it.kind);
      const t = (now - it.startedAt) / lifetime;
      if (t >= 1) return false;
      if      (it.kind === 'ring')        drawRing(ctx, it, t);
      else if (it.kind === 'burst')       drawBurst(ctx, it, t);
      else if (it.kind === 'glow')        drawGlow(ctx, it, t);
      else /* invoke-glow */              drawInvokeGlow(ctx, it, t);
      return true;
    });
  }

  clear(): void { this._items.length = 0; }

  get active(): boolean { return this._items.length > 0; }
}

// ── Per-kind renderers ──────────────────────────────────────────────────────

function defaultColorFor(kind: TapEffectKind): string {
  if (kind === 'ring')         return RING_DEFAULT_COLOR;
  if (kind === 'burst')        return BURST_DEFAULT_COLOR;
  if (kind === 'invoke-glow')  return INVOKE_GLOW_CAST_COLOR;
  return TAP_NEUTRAL_COLOR;    // glow
}

function lifetimeFor(kind: TapEffectKind): number {
  if (kind === 'glow')         return GLOW_DURATION_MS;
  if (kind === 'invoke-glow')  return INVOKE_GLOW_DURATION_MS;
  return TAP_FX_DURATION_MS;
}

/** Outline expanding circle: subtle pulse, non-occluding. */
function drawRing(
  ctx: CanvasRenderingContext2D, it: TapEffectInstance, t: number,
): void {
  const eased = 1 - (1 - t) ** 3;
  const baseR = RING_BASE_R * it.scale;
  const ringR = baseR * (1 + 0.35 * eased);
  const alpha = (1 - t) * 0.55;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = it.color;
  ctx.lineWidth = 1.2 * (1 - t * 0.6);
  ctx.beginPath();
  ctx.arc(it.cx, it.cy, ringR, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/** Solid filled disc that expands with a bright alpha flash then fades.
 *  Math copied verbatim from the original tap-ghost consumed-state
 *  rendering — same look, just relocatable. */
function drawBurst(
  ctx: CanvasRenderingContext2D, it: TapEffectInstance, t: number,
): void {
  const baseR = BURST_BASE_R * it.scale;
  const peakR = BURST_PEAK_R * it.scale;
  const r = baseR + (peakR - baseR) * t;
  const alpha = t < 0.2 ? 0.9 : 0.9 * Math.max(0, 1 - (t - 0.2) / 0.8);
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = it.color;
  ctx.beginPath();
  ctx.arc(it.cx, it.cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Neutral radial-gradient pulse — same family as invoke-glow but a
 *  single tint (faint white by default), shorter lifetime, smaller
 *  peak radius. Used for every confirmed tap that doesn't resolve an
 *  invocation. */
function drawGlow(
  ctx: CanvasRenderingContext2D, it: TapEffectInstance, t: number,
): void {
  const baseR = GLOW_BASE_R * it.scale;
  const peakR = GLOW_PEAK_R * it.scale;
  const bloom = 1 - (1 - t) ** 3;
  const r = baseR + (peakR - baseR) * bloom;
  const alpha = t < 0.18 ? (t / 0.18) * 0.55 : 0.55 * Math.max(0, 1 - (t - 0.18) / 0.82);
  if (alpha <= 0.01) return;
  const grad = ctx.createRadialGradient(it.cx, it.cy, 0, it.cx, it.cy, r);
  grad.addColorStop(0,   withAlpha(it.color, alpha));
  grad.addColorStop(0.5, withAlpha(it.color, alpha * 0.4));
  grad.addColorStop(1,   withAlpha(it.color, 0));
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(it.cx, it.cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Radial-gradient pulse for an invocation resolve. Same family as
 *  drawGlow but with a richer color arrangement — gold core fading
 *  through warm amber into the invocation's flavour tint (purple
 *  for cast, blue for stylus menu) — and a longer lifetime so the
 *  climax breathes rather than flashes. */
function drawInvokeGlow(
  ctx: CanvasRenderingContext2D, it: TapEffectInstance, t: number,
): void {
  const baseR = INVOKE_GLOW_BASE_R * it.scale;
  const peakR = INVOKE_GLOW_PEAK_R * it.scale;
  const bloom = 1 - (1 - t) ** 3;             // quick bloom
  const r = baseR + (peakR - baseR) * bloom;
  const alpha = t < 0.18 ? (t / 0.18) * 0.9 : 0.9 * Math.max(0, 1 - (t - 0.18) / 0.82);
  if (alpha <= 0.01) return;
  // Gold core (matches cast-stroke tail glow) → invocation-colour
  // mid → transparent edge. The tint reads on the halo as the bloom
  // expands.
  const grad = ctx.createRadialGradient(it.cx, it.cy, 0, it.cx, it.cy, r);
  grad.addColorStop(0,    `rgba(255, 213, 79, ${alpha * 0.65})`);   // gold core
  grad.addColorStop(0.35, `rgba(200, 168, 74, ${alpha * 0.40})`);   // warm gold mid
  grad.addColorStop(0.7,  withAlpha(it.color, alpha * 0.45));       // invocation tint
  grad.addColorStop(1,    withAlpha(it.color, 0));
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(it.cx, it.cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Convert a #rrggbb color to rgba(...) at the given alpha. */
function withAlpha(hex: string, a: number): string {
  if (hex.startsWith('#') && hex.length === 7) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  return hex;
}
